import { useState } from 'react';
import { File as FileGlyph, FileArchive, FileImage, FileSpreadsheet, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { MAX_ATTACHMENTS, MAX_ATTACHMENT_SIZE } from '../../lib/aiJobAttachments';

/**
 * Files picked for an agent job before the job exists — the spec queue panels
 * and the Content brief. This hook owns the list and its checks; the
 * AttachmentDropzone renders it. Uploads happen at enqueue time
 * (enqueueAiJobWithFiles).
 */

export interface PendingAttachment {
  id: string;
  file: File;
}

const TEXT_EXT = ['pdf', 'md', 'markdown', 'txt', 'rtf', 'doc', 'docx', 'json', 'xml', 'yaml', 'yml', 'html', 'htm', 'ppt', 'pptx'];
const SHEET_EXT = ['csv', 'tsv', 'xls', 'xlsx'];

export function glyphFor(name: string, type: string): LucideIcon {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (type.startsWith('image/')) return FileImage;
  if (type.includes('zip') || ext === 'zip') return FileArchive;
  if (type.includes('csv') || type.includes('spreadsheet') || type.includes('excel') || SHEET_EXT.includes(ext)) {
    return FileSpreadsheet;
  }
  if (type.includes('pdf') || type.includes('text') || type.includes('document') || TEXT_EXT.includes(ext)) {
    return FileText;
  }
  return FileGlyph;
}

export function useAttachmentPicker() {
  const [files, setFiles] = useState<PendingAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const add = (list: FileList | File[]) => {
    const accepted: PendingAttachment[] = [];
    const problems: string[] = [];
    for (const file of Array.from(list)) {
      if (file.size > MAX_ATTACHMENT_SIZE) {
        problems.push(`“${file.name}” is over 25 MB`);
        continue;
      }
      const dupe = [...files, ...accepted].some((f) => f.file.name === file.name && f.file.size === file.size);
      if (dupe) continue;
      if (files.length + accepted.length >= MAX_ATTACHMENTS) {
        problems.push(`Up to ${MAX_ATTACHMENTS} files`);
        break;
      }
      accepted.push({ id: crypto.randomUUID(), file });
    }
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
    setError(problems.length > 0 ? problems.join(' · ') : null);
  };

  const remove = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setError(null);
  };

  const clear = () => {
    setFiles([]);
    setError(null);
  };

  return { files, error, add, remove, clear };
}

export type AttachmentPicker = ReturnType<typeof useAttachmentPicker>;
